import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { RedirectIfAuthed, RequireAuth } from "@/components/AuthGate";
import { RequireOnboarding } from "@/components/RequireOnboarding";
import { RouteCanonical } from "@/components/RouteCanonical";
import { RouteSeo } from "@/components/RouteSeo";
import { ErrorBoundary, RouteErrorBoundary } from "@/components/ErrorBoundary";
import { legacyRedirectRoutes } from "@/components/LegacyRedirects";
import NotFound from "./pages/NotFound.tsx";
import Forbidden from "./pages/Forbidden.tsx";
import RoleError from "./pages/RoleError.tsx";

import { CoachApprovalGate } from "@/components/CoachApprovalGate";
import { StripeModeIndicator } from "@/components/StripeModeIndicator";

// Every meaningful page is lazy-loaded so the initial JS payload (especially
// for first-time landing visitors) stays as small as possible. Only tiny
// always-needed shells (NotFound, Forbidden, RoleError) ship in the entry.
const Landing = lazy(() => import("./pages/Landing.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const SignupClient = lazy(() => import("./pages/SignupClient.tsx"));
const SignupCoach = lazy(() => import("./pages/SignupCoach.tsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Index = lazy(() => import("./pages/Index.tsx"));
const ClientOnboarding = lazy(() => import("./pages/ClientOnboarding.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const AdminPanel = lazy(() => import("./pages/AdminPanel.tsx"));
const PaymentsAdmin = lazy(() => import("./pages/PaymentsAdmin.tsx"));
const AdminWebhookDebug = lazy(() => import("./pages/AdminWebhookDebug.tsx"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions.tsx"));
const AdminEmailEvents = lazy(() => import("./pages/AdminEmailEvents.tsx"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog.tsx"));
const AdminDiagnostics = lazy(() => import("./pages/AdminDiagnostics.tsx"));
const AdminStripeAudit = lazy(() => import("./pages/AdminStripeAudit.tsx"));
const AdminCloudAuth = lazy(() => import("./pages/AdminCloudAuth.tsx"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard.tsx"));
const CoachClientDetail = lazy(() => import("./pages/CoachClientDetail.tsx"));
const CoachMessages = lazy(() => import("./pages/CoachMessages.tsx"));
const CoachLinkClient = lazy(() => import("./pages/CoachLinkClient.tsx"));
const CoachProfileEdit = lazy(() => import("./pages/CoachProfileEdit.tsx"));

const CoachReferrals = lazy(() => import("./pages/CoachReferrals.tsx"));
const CoachPayments = lazy(() => import("./pages/CoachPayments.tsx"));
const CoachPricing = lazy(() => import("./pages/CoachPricing.tsx"));
const CoachOnboarding = lazy(() => import("./pages/CoachOnboarding.tsx"));
const CoachAccount = lazy(() => import("./pages/CoachAccount.tsx"));
const CoachInvoices = lazy(() => import("./pages/CoachInvoices.tsx"));
const CoachEarnings = lazy(() => import("./pages/CoachEarnings.tsx"));
const CoachSignupTracking = lazy(() => import("./pages/CoachSignupTracking.tsx"));
const CoachBusinessOverview = lazy(() => import("./pages/CoachBusinessOverview.tsx"));
const PayCoachInvoice = lazy(() => import("./pages/PayCoachInvoice.tsx"));
const MyInvoices = lazy(() => import("./pages/MyInvoices.tsx"));
const AccountBilling = lazy(() => import("./pages/AccountBilling.tsx"));
const ClientPortal = lazy(() => import("./pages/ClientPortal.tsx"));
const MyProgress = lazy(() => import("./pages/MyProgress.tsx"));
const MyPlan = lazy(() => import("./pages/MyPlan.tsx"));
const MyPlanBuilder = lazy(() => import("./pages/MyPlanBuilder.tsx"));
const MyGym = lazy(() => import("./pages/MyGym.tsx"));
const Rehab = lazy(() => import("./pages/Rehab.tsx"));
const MyFood = lazy(() => import("./pages/MyFood.tsx"));
const AthleteApp = lazy(() => import("./pages/AthleteApp.tsx"));
const CoachAIPlanBuilder = lazy(() => import("./pages/CoachAIPlanBuilder.tsx"));
const LinkCoach = lazy(() => import("./pages/LinkCoach.tsx"));
const CoachMessageHistory = lazy(() => import("./pages/CoachMessageHistory.tsx"));
const ClientCoachDashboard = lazy(() => import("./pages/ClientCoachDashboard.tsx"));

const CoachBuilderHub = lazy(() => import("./pages/CoachBuilderHub.tsx"));
const CoachWorkoutBuilder = lazy(() => import("./pages/CoachWorkoutBuilder.tsx"));
const CoachMealBuilder = lazy(() => import("./pages/CoachMealBuilder.tsx"));
const CompetitionPlanBuilder = lazy(() => import("./pages/CompetitionPlanBuilder.tsx"));
const ShowCalendarBuilder = lazy(() => import("./pages/ShowCalendarBuilder.tsx"));
const PhaseGuide = lazy(() => import("./pages/PhaseGuide.tsx"));
const CoachTemplates = lazy(() => import("./pages/CoachTemplates.tsx"));
const CoachRehabTemplates = lazy(() => import("./pages/CoachRehabTemplates.tsx"));
const CoachClients = lazy(() => import("./pages/CoachClients.tsx"));
const CoachCalendar = lazy(() => import("./pages/CoachCalendar.tsx"));
const CoachProgressLog = lazy(() => import("./pages/CoachProgressLog.tsx"));
const CoachProgressReport = lazy(() => import("./pages/CoachProgressReport.tsx"));
const CoachGoals = lazy(() => import("./pages/CoachGoals.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const CheckoutUnavailablePage = lazy(() => import("./pages/CheckoutUnavailablePage.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const PersonalizedWorkoutPlans = lazy(
  () => import("./pages/PersonalizedWorkoutPlans.tsx"),
);
const AiNutritionPlan = lazy(() => import("./pages/AiNutritionPlan.tsx"));
const AiPersonalTrainer = lazy(() => import("./pages/AiPersonalTrainer.tsx"));
const AiWorkoutGenerator = lazy(() => import("./pages/AiWorkoutGenerator.tsx"));
const AiPosingCoach = lazy(() => import("./pages/AiPosingCoach.tsx"));
const HowItWorks = lazy(() => import("./pages/HowItWorks.tsx"));
const HowWeUseAi = lazy(() => import("./pages/HowWeUseAi.tsx"));
const AthletePortalLanding = lazy(() => import("./pages/AthletePortal.tsx"));
const GuidesIndex = lazy(() => import("./pages/Guides.tsx"));
const GuideArticle = lazy(() => import("./pages/GuideArticle.tsx"));
const Locations = lazy(() => import("./pages/Locations.tsx"));
const LocationCity = lazy(() => import("./pages/LocationCity.tsx"));
const CoachDirectory = lazy(() => import("./pages/CoachDirectory.tsx"));
const CoachProfile = lazy(() => import("./pages/CoachProfile.tsx"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe.tsx"));
const HealthCheck = lazy(() => import("./pages/HealthCheck.tsx"));
const Challenges = lazy(() => import("./pages/Challenges.tsx"));
const ChallengeNew = lazy(() => import("./pages/ChallengeNew.tsx"));
const ChallengeDetail = lazy(() => import("./pages/ChallengeDetail.tsx"));
const ChallengeCheckin = lazy(() => import("./pages/ChallengeCheckin.tsx"));
const ChallengeJoin = lazy(() => import("./pages/ChallengeJoin.tsx"));
const MCPConsent = lazy(() => import("./pages/MCPConsent.tsx"));
const RefLanding = lazy(() => import("./pages/RefLanding"));
const AffiliateApply = lazy(() => import("./pages/AffiliateApply"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const AdminAffiliates = lazy(() => import("./pages/AdminAffiliates"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const AffiliatePosts = lazy(() => import("./pages/AffiliatePosts"));
const AthleteBlog = lazy(() => import("./pages/AthleteBlog"));
const AthleteBlogPost = lazy(() => import("./pages/AthleteBlogPost"));

const RouteFallback = () => (
  <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "#888", fontSize: 14 }}>
    Loading…
  </div>
);

// Cache reads for a few minutes and don't refetch just because the window
// regained focus — switching tabs inside the app used to re-request everything.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <StripeModeIndicator />
            <PaywallProvider>
            <AnalyticsProvider>
              <RouteCanonical />
              <RouteSeo />
            <RouteErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />

              <Route
                path="/auth"
                element={
                  <>
                    <RedirectIfAuthed />
                    <Login />
                  </>
                }
              />
              <Route
                path="/signup-client"
                element={
                  <>
                    <RedirectIfAuthed />
                    <SignupClient />
                  </>
                }
              />
              <Route
                path="/signup-coach"
                element={
                  <>
                    <RedirectIfAuthed />
                    <SignupCoach />
                  </>
                }
              />
              {/* Coach-shared onboarding link → reuse the client signup flow
                  with the invite code pre-filled. */}
              <Route
                path="/onboard/:code"
                element={
                  <>
                    <RedirectIfAuthed />
                    <SignupClient />
                  </>
                }
              />

              <Route
                path="/forgot-password"
                element={
                  <>
                    <RedirectIfAuthed />
                    <ForgotPassword />
                  </>
                }
              />
              {/* /reset-password intentionally has NO RedirectIfAuthed —
                  Supabase establishes a short-lived recovery session from
                  the email link, which would otherwise bounce the user away. */}
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Required signup questionnaire for brand-new athletes. */}
              <Route
                path="/welcome"
                element={
                  <RequireAuth role={["client", "coach"]}>
                    <ClientOnboarding />
                  </RequireAuth>
                }
              />
              <Route
                path="/app"
                element={
                  <RequireAuth role={["client", "coach"]}>
                    <RequireOnboarding>
                      <Index />
                    </RequireOnboarding>
                  </RequireAuth>
                }
              />
              <Route
                path="/coach"
                element={
                  <RequireAuth role="coach">
                    <CoachApprovalGate>
                      <CoachDashboard />
                    </CoachApprovalGate>
                  </RequireAuth>
                }
              />
              <Route
                path="/coach/clients/:id"
                element={
                  <RequireAuth role="coach">
                    <CoachApprovalGate>
                      <CoachClientDetail />
                    </CoachApprovalGate>
                  </RequireAuth>
                }
              />
              <Route
                path="/coach/messages"
                element={
                  <RequireAuth role="coach">
                    <CoachApprovalGate>
                      <CoachMessages />
                    </CoachApprovalGate>
                  </RequireAuth>
                }
              />
              <Route
                path="/coach/link-client"
                element={
                  <RequireAuth role="coach">
                    <CoachApprovalGate>
                      <CoachLinkClient />
                    </CoachApprovalGate>
                  </RequireAuth>
                }
              />
              {/* Profile setup is available while the application is pending,
                  so new coaches can add their photo + gallery straight away. */}
              <Route path="/coach/profile" element={<RequireAuth role="coach"><CoachProfileEdit /></RequireAuth>} />

              {/* Roster, overview and competition tracker now live as sections of /coach/clients. */}
              <Route path="/coach/roster" element={<Navigate to="/coach/clients?view=overview" replace />} />
              <Route path="/coach/referrals" element={<RequireAuth role="coach"><CoachApprovalGate><CoachReferrals /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/payments" element={<RequireAuth role="coach"><CoachApprovalGate><CoachPayments /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/pricing" element={<RequireAuth role="coach"><CoachApprovalGate><CoachPricing /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/onboarding" element={<RequireAuth role="coach"><CoachApprovalGate><CoachOnboarding /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/account" element={<RequireAuth role="coach"><CoachApprovalGate><CoachAccount /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/invoices" element={<RequireAuth role="coach"><CoachApprovalGate><CoachInvoices /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/signup-tracking" element={<RequireAuth role="coach"><CoachApprovalGate><CoachSignupTracking /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/earnings" element={<RequireAuth role="coach"><CoachApprovalGate><CoachEarnings /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/overview" element={<RequireAuth role="coach"><CoachApprovalGate><CoachBusinessOverview /></CoachApprovalGate></RequireAuth>} />
              <Route path="/invoice/:invoiceId" element={<PayCoachInvoice />} />
              <Route path="/invoices" element={<RequireAuth role={["client", "coach"]}><MyInvoices /></RequireAuth>} />
              <Route path="/portal" element={<RequireAuth role={["client", "coach"]}><RequireOnboarding><ClientPortal /></RequireOnboarding></RequireAuth>} />
              <Route path="/my-account" element={<Navigate to="/portal" replace />} />
              <Route path="/account/billing" element={<RequireAuth role={["client", "coach"]}><AccountBilling /></RequireAuth>} />
              <Route path="/coach/message-history" element={<RequireAuth role="coach"><CoachApprovalGate><CoachMessageHistory /></CoachApprovalGate></RequireAuth>} />
              <Route path="/my-coach" element={<RequireAuth role={["client", "coach"]}><ClientCoachDashboard /></RequireAuth>} />
              <Route path="/link-coach" element={<RequireAuth role={["client", "coach"]}><LinkCoach /></RequireAuth>} />
              <Route path="/my-progress" element={<RequireAuth role={["client", "coach"]}><MyProgress /></RequireAuth>} />
              <Route path="/my-plan" element={<RequireAuth role={["client", "coach"]}><MyPlan /></RequireAuth>} />
              <Route path="/my-plan/build" element={<RequireAuth role={["client", "coach"]}><MyPlanBuilder /></RequireAuth>} />
              <Route path="/my-gym" element={<RequireAuth role={["client", "coach"]}><MyGym /></RequireAuth>} />
              <Route path="/rehab" element={<RequireAuth role={["client", "coach"]}><Rehab /></RequireAuth>} />
              <Route path="/my-food" element={<RequireAuth role={["client", "coach"]}><MyFood /></RequireAuth>} />
              <Route path="/athlete" element={<RequireAuth role={["client", "coach"]}><AthleteApp /></RequireAuth>} />
              <Route path="/coach/builder" element={<RequireAuth role="coach"><CoachApprovalGate><CoachBuilderHub /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/builder/ai" element={<RequireAuth role="coach"><CoachApprovalGate><CoachAIPlanBuilder /></CoachApprovalGate></RequireAuth>} />

              <Route path="/coach/builder/workout/:clientId" element={<RequireAuth role="coach"><CoachApprovalGate><CoachWorkoutBuilder /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/builder/meal/:clientId" element={<RequireAuth role="coach"><CoachApprovalGate><CoachMealBuilder /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/builder/competition/:clientId" element={<RequireAuth role="coach"><CoachApprovalGate><CompetitionPlanBuilder /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/builder/calendar/:clientId" element={<RequireAuth role="coach"><CoachApprovalGate><ShowCalendarBuilder /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/builder/guide" element={<RequireAuth role="coach"><CoachApprovalGate><PhaseGuide /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/builder/guide/:clientId" element={<RequireAuth role="coach"><CoachApprovalGate><PhaseGuide /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/templates" element={<RequireAuth role="coach"><CoachApprovalGate><CoachTemplates /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/rehab-templates" element={<RequireAuth role="coach"><CoachApprovalGate><CoachRehabTemplates /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/overview" element={<Navigate to="/coach/clients?view=overview" replace />} />
              <Route path="/coach/calendar" element={<RequireAuth role="coach"><CoachApprovalGate><CoachCalendar /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/progress" element={<RequireAuth role="coach"><CoachApprovalGate><CoachProgressLog /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/progress-report" element={<RequireAuth role="coach"><CoachApprovalGate><CoachProgressReport /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/goals" element={<RequireAuth role="coach"><CoachApprovalGate><CoachGoals /></CoachApprovalGate></RequireAuth>} />
              <Route path="/coach/competition" element={<Navigate to="/coach/clients?view=competition" replace />} />
              <Route path="/coach/clients" element={<RequireAuth role="coach"><CoachApprovalGate><CoachClients /></CoachApprovalGate></RequireAuth>} />


              {/* Admin — admin-only */}
              <Route
                path="/admin"
                element={
                  <RequireAuth role="admin">
                    <AdminDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/legacy"
                element={
                  <RequireAuth role="admin">
                    <AdminPanel />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <RequireAuth role="admin">
                    <PaymentsAdmin />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/webhook-debug"
                element={
                  <RequireAuth role="admin">
                    <AdminWebhookDebug />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <RequireAuth role="admin">
                    <AdminSubscriptions />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/email-log"
                element={
                  <RequireAuth role="admin">
                    <AdminEmailEvents />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/audit-log"
                element={
                  <RequireAuth role="admin">
                    <AdminAuditLog />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/diagnostics"
                element={
                  <RequireAuth role="admin">
                    <AdminDiagnostics />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/stripe-audit"
                element={
                  <RequireAuth role="admin">
                    <AdminStripeAudit />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/cloud-auth"
                element={
                  <RequireAuth role="admin">
                    <AdminCloudAuth />
                  </RequireAuth>
                }
              />

              <Route path="/pricing" element={<Pricing />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/checkout/unavailable" element={<CheckoutUnavailablePage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/about" element={<About />} />
              <Route
                path="/personalized-workout-plans"
                element={<PersonalizedWorkoutPlans />}
              />
              <Route path="/ai-nutrition-plan" element={<AiNutritionPlan />} />
              <Route path="/ai-personal-trainer" element={<AiPersonalTrainer />} />
              <Route path="/ai-workout-generator" element={<AiWorkoutGenerator />} />
              <Route path="/ai-posing-coach" element={<AiPosingCoach />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/how-we-use-ai" element={<HowWeUseAi />} />
              <Route path="/athlete-portal" element={<AthletePortalLanding />} />
              <Route path="/guides" element={<GuidesIndex />} />
              <Route path="/guides/:slug" element={<GuideArticle />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:slug" element={<LocationCity />} />
              <Route path="/coaches" element={<CoachDirectory />} />
              <Route path="/c/:username" element={<CoachProfile />} />
              <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
              {/* Operator-only runtime probe — not for end users. */}
              <Route path="/health" element={<RequireAuth role="admin"><HealthCheck /></RequireAuth>} />
              <Route path="/.lovable/oauth/consent" element={<MCPConsent />} />

              {/* Transformation Challenges (athlete-facing) */}
              <Route path="/challenges" element={<RequireAuth><Challenges /></RequireAuth>} />
              <Route path="/challenges/new" element={<RequireAuth><ChallengeNew /></RequireAuth>} />
              <Route path="/challenges/join/:code" element={<ChallengeJoin />} />
              <Route path="/challenges/:id" element={<RequireAuth><ChallengeDetail /></RequireAuth>} />
              <Route path="/challenges/:id/checkin" element={<RequireAuth><ChallengeCheckin /></RequireAuth>} />


              {/* Athlete affiliate program */}
              <Route path="/ref/:code" element={<RefLanding />} />
              <Route path="/affiliate/apply" element={<RequireAuth><AffiliateApply /></RequireAuth>} />
              <Route path="/affiliate" element={<RequireAuth><AffiliateDashboard /></RequireAuth>} />
              <Route path="/affiliate/posts" element={<RequireAuth><AffiliatePosts /></RequireAuth>} />

              {/* Public athlete blog */}
              <Route path="/blog" element={<AthleteBlog />} />
              <Route path="/blog/:slug" element={<AthleteBlogPost />} />
              <Route
                path="/admin/referrals"
                element={
                  <RequireAuth role="admin">
                    <AdminReferrals />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/affiliates"
                element={
                  <RequireAuth role="admin">
                    <AdminAffiliates />
                  </RequireAuth>
                }
              />

              <Route path="/403" element={<Forbidden />} />
              <Route path="/role-error" element={<RoleError />} />

              {/* Client-side redirects from legacy URLs. Defined in
                  src/lib/legacyRedirects.ts — empty by default. */}
              {legacyRedirectRoutes()}

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
            </AnalyticsProvider>
            </PaywallProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
